import { AlertCircle, CheckCircle, Clock } from 'lucide-react'

// All supported destination countries
export const CPN_COUNTRIES = [
  'BR', // Brazil
  'HK', // Hong Kong
  'MX', // Mexico
  'CN', // China
  'CO', // Colombia
  'IN', // India
  'NG', // Nigeria
  'CA', // Canada
  'US', // United States
  'GB', // United Kingdom
  'DE', // Germany
  'FR', // France
  'IT', // Italy
  'ES', // Spain
  'AU', // Australia
  'JP', // Japan
  'KR', // South Korea
  'SG', // Singapore
  'TH', // Thailand
  'MY', // Malaysia
  'PH', // Philippines
  'ID', // Indonesia
  'VN', // Vietnam
] as const

export type CpnCountryCode = (typeof CPN_COUNTRIES)[number]

// All possible payment methods from the API
export const CPN_PAYMENT_METHODS = [
  'BANK-TRANSFER',
  'CHATS',
  'FPS',
  'PIX',
  'SPEI',
  'WIRE',
  'NEQUI',
  'CIPS',
  'IMPS',
  'NEFT',
  'RTGS',
  'PESONET',
  'INSTAPAY',
] as const

export type CpnPaymentMethod = (typeof CPN_PAYMENT_METHODS)[number]

// Payment Method Display Information (updated with all available methods)
export const PAYMENT_METHOD_INFO: Record<
  CpnPaymentMethod,
  {
    name: string
    icon: string
    logo?: string
    description?: string
    regions?: CpnCountryCode[]
    status: 'confirmed' | 'limited'
    speed: number // Higher number = faster (1-10 scale)
  }
> = {
  PIX: {
    name: 'PIX Transfer',
    icon: 'PIX',
    logo: '/assets/pix.png',
    description: 'Brazilian instant payment system',
    regions: ['BR'],
    status: 'confirmed',
    speed: 10, // Instant
  },
  FPS: {
    name: 'Faster Payment System',
    icon: 'FPS',
    logo: '/assets/fps_color.svg',
    description: 'Hong Kong instant payment system',
    regions: ['HK'],
    status: 'confirmed',
    speed: 10, // Instant
  },
  CHATS: {
    name: 'CHATS Transfer',
    icon: 'CHATS',
    logo: '/assets/chats_svg.jpeg',
    description: 'Clearing House Automated Transfer System',
    regions: ['HK'],
    status: 'confirmed',
    speed: 7, // Same-day settlement
  },
  'BANK-TRANSFER': {
    name: 'Bank Transfer',
    icon: 'BANK',
    description: 'Standard bank transfer',
    regions: ['NG', 'CO'],
    status: 'confirmed',
    speed: 5, // 1-3 business days
  },
  SPEI: {
    name: 'SPEI Transfer',
    icon: 'SPEI',
    logo: '/assets/spei.svg',
    description: 'Mexican electronic payment system',
    regions: ['MX'],
    status: 'confirmed',
    speed: 9, // Near-instant
  },
  WIRE: {
    name: 'Wire Transfer',
    icon: 'WIRE',
    logo: '/assets/wire.jpg',
    description: 'International wire transfer',
    regions: ['BR', 'HK', 'MX', 'CN'],
    status: 'confirmed',
    speed: 3, // 1-5 business days
  },
  NEQUI: {
    name: 'Nequi Transfer',
    icon: 'NEQUI',
    logo: '/assets/nequi.png',
    description:
      'Colombian digital payment method and e-wallet for QR code payments',
    regions: ['CO'],
    status: 'confirmed',
    speed: 10, // Instant
  },
  CIPS: {
    name: 'CIPS Transfer',
    icon: 'CIPS',
    logo: '/assets/cips.jpeg',
    description: 'Cross-border Interbank Payment System for China',
    regions: ['CN'],
    status: 'confirmed',
    speed: 6, // 1-2 business days
  },
  IMPS: {
    name: 'IMPS Transfer',
    icon: 'IMPS',
    logo: '/assets/imps.png',
    description:
      'Immediate Payment Service for instant bank transfers in India',
    regions: ['IN'],
    status: 'confirmed',
    speed: 10, // Instant (24/7)
  },
  NEFT: {
    name: 'NEFT Transfer',
    icon: 'NEFT',
    logo: '/assets/neft.png',
    description: 'National Electronic Funds Transfer for India',
    regions: ['IN'],
    status: 'confirmed',
    speed: 7, // Hourly batches, same-day
  },
  RTGS: {
    name: 'RTGS Transfer',
    icon: 'RTGS',
    logo: '/assets/rtgs.png',
    description: 'Real Time Gross Settlement for high-value transfers in India',
    regions: ['IN'],
    status: 'confirmed',
    speed: 6, // Real-time but business hours only
  },
  PESONET: {
    name: 'PESONet Transfer',
    icon: 'PESONET',
    logo: '/assets/pesonet.png',
    description:
      'Philippine Electronic Fund Transfer System and Operations Network',
    regions: ['PH'],
    status: 'confirmed',
    speed: 6, // Slower: depends on bank cut-off (usually 2-6 hours on workdays)
  },
  INSTAPAY: {
    name: 'InstaPay Transfer',
    icon: 'INSTAPAY',
    logo: '/assets/instapay.png',
    description:
      'Instant, secure transfers between banks, digital wallets, and cards',
    regions: ['PH'],
    status: 'confirmed',
    speed: 9, // Near instant - most transfers are completed instantly or within 30 minutes
  },
}

// Helper function to get payment method display info
export const getPaymentMethodInfo = (method: string) => {
  return (
    PAYMENT_METHOD_INFO[method as CpnPaymentMethod] || {
      name: method,
      icon: 'PAY',
      logo: undefined,
      description: 'Payment method',
      speed: 5, // Default medium speed
    }
  )
}

// Helper function to get the fastest payment method from an array
export const getFastestPaymentMethod = (
  methods: CpnPaymentMethod[]
): CpnPaymentMethod | undefined => {
  if (!methods || methods.length === 0) return undefined

  // Create a copy to avoid mutating the original array
  const methodsCopy = [...methods]

  // Sort by speed (highest first) and return the fastest
  return methodsCopy.sort((a, b) => {
    const speedA = PAYMENT_METHOD_INFO[a]?.speed || 0
    const speedB = PAYMENT_METHOD_INFO[b]?.speed || 0
    return speedB - speedA // Descending order (fastest first)
  })[0]
}

// CPN Quote Types
export const CPN_QUOTE_TYPES = ['fromExact', 'toExact'] as const
export type CpnQuoteType = (typeof CPN_QUOTE_TYPES)[number]

// CPN Use Cases
export const CPN_USE_CASES = ['B2B', 'B2C', 'C2B', 'C2C'] as const
export type CpnUseCase = (typeof CPN_USE_CASES)[number]

// CPN Payment Reason Codes
export const CPN_PAYMENT_REASONS = [
  'PMT001', // Invoice payments
  'PMT002', // Payment for services
  'PMT003', // Payment for software
  'PMT004', // Payment for imported goods
  'PMT005', // Travel services
  'PMT006', // Transfer to own account
  'PMT007', // Repayment of loans
  'PMT008', // Payroll
  'PMT009', // Payment of property rental
  'PMT010', // Information service charges
  'PMT011', // Advertising & public relations related expenses
  'PMT012', // Royalty fees, trademark fees, patent fees, and copyright fees
  'PMT013', // Fees for brokers, front end fee, commitment fee, guarantee fee and custodian fee
  'PMT014', // Fees for advisors, technical assistance, and academic knowledge, including remuneration for specialists
  'PMT015', // Representative office expenses
  'PMT016', // Tax payment
  'PMT017', // Transportation fees for goods
  'PMT018', // Construction costs/expenses
  'PMT019', // Insurance premium
  'PMT020', // General goods trades - offline trade
  'PMT021', // Insurance claims payment
  'PMT022', // Remittance payments to friends/family
  'PMT023', // Education-related student expenses
  'PMT024', // Medical treatment
  'PMT025', // Donations
  'PMT026', // Mutual fund investment
  'PMT027', // Currency exchange
  'PMT028', // Advance payments for goods
  'PMT029', // Merchant settlement
  'PMT030', // Repatriation fund settlement
] as const

export type CpnPaymentReason = (typeof CPN_PAYMENT_REASONS)[number]

// Payment Reason Labels
export const PAYMENT_REASON_LABELS: Record<CpnPaymentReason, string> = {
  PMT001: 'Invoice payments',
  PMT002: 'Payment for services',
  PMT003: 'Payment for software',
  PMT004: 'Payment for imported goods',
  PMT005: 'Travel services',
  PMT006: 'Transfer to own account',
  PMT007: 'Repayment of loans',
  PMT008: 'Payroll',
  PMT009: 'Payment of property rental',
  PMT010: 'Information service charges',
  PMT011: 'Advertising & public relations related expenses',
  PMT012: 'Royalty fees, trademark fees, patent fees, and copyright fees',
  PMT013:
    'Fees for brokers, front end fee, commitment fee, guarantee fee and custodian fee',
  PMT014: 'Fees for advisors, technical assistance, and academic knowledge',
  PMT015: 'Representative office expenses',
  PMT016: 'Tax payment',
  PMT017: 'Transportation fees for goods',
  PMT018: 'Construction costs/expenses',
  PMT019: 'Insurance premium',
  PMT020: 'General goods trades - offline trade',
  PMT021: 'Insurance claims payment',
  PMT022: 'Remittance payments to friends/family',
  PMT023: 'Education-related student expenses',
  PMT024: 'Medical treatment',
  PMT025: 'Donations',
  PMT026: 'Mutual fund investment',
  PMT027: 'Currency exchange',
  PMT028: 'Advance payments for goods',
  PMT029: 'Merchant settlement',
  PMT030: 'Repatriation fund settlement',
}

// Magic Words for payment processing
export const CPN_MAGIC_WORDS = ['Delayed', 'Immediate', 'Scheduled'] as const

export type CpnMagicWord = (typeof CPN_MAGIC_WORDS)[number]

// RFI Magic Words
export const CPN_RFI_MAGIC_WORDS = ['InReview', 'Approved', 'Rejected'] as const

export type CpnRfiMagicWord = (typeof CPN_RFI_MAGIC_WORDS)[number]

// Types for dynamic route configuration from API
export interface DynamicCurrency {
  code: string
  paymentMethods: CpnPaymentMethod[]
  defaultPaymentMethod: CpnPaymentMethod
  status: 'confirmed' | 'limited'
  supportedBlockchains: string[]
  amountLimits: {
    min: number
    max: number
  }
  lastUpdated: string
}

export interface DynamicCpnRoute {
  name: string
  currencies: DynamicCurrency[]
  localCurrency: string
  flag: string
  description: string
}

export interface DynamicDestinationOption {
  countryCode: string
  currency: string
  name: string
  flag: string
  paymentMethods: CpnPaymentMethod[]
  defaultPaymentMethod: CpnPaymentMethod
  isLocal: boolean
  description: string
  status: 'confirmed' | 'limited'
  amountLimits: {
    min: number
    max: number
  }
  supportedBlockchains: string[]
  lastUpdated: string
}

// CPN Source Configuration (updated for live API - CAD based)
export const CPN_SOURCE_CONFIG = {
  senderCountry: 'CA' as const,
  sourceCurrencies: ['CAD'] as const,
  defaultSourceCurrency: 'CAD' as const,
} as const

export type CpnSourceCurrency =
  (typeof CPN_SOURCE_CONFIG.sourceCurrencies)[number]

export type StatusInfo = {
  label: string
  description: string
  Icon: React.ElementType
  color: string // e.g., 'text-blue-600'
  bgColor: string // e.g., 'bg-blue-50'
  gradient?: string
}

export const paymentStatusMap: Record<string, StatusInfo> = {
  // Initial state: User has submitted the transfer
  CREATED: {
    label: 'Initiated',
    description:
      "We've received your transfer request and are preparing it for processing.",
    Icon: Clock,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    gradient: 'from-blue-500 to-indigo-500',
  },

  // Intermediate state: This covers the crypto leg of the journey
  CRYPTO_FUNDS_PENDING: {
    label: 'Processing',
    description:
      "Your funds are securely in transit to the recipient's country.",
    Icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    gradient: 'from-orange-400 to-amber-600',
  },

  // Final processing state: Payout to the recipient's bank
  FIAT_PAYMENT_INITIATED: {
    label: 'Processing',
    description:
      'Your transfer is being finalized and will be delivered shortly.',
    Icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    gradient: 'from-orange-400 to-amber-600',
  },

  // Success state: Money has arrived
  COMPLETED: {
    label: 'Sent',
    description: 'The funds have been successfully delivered to the recipient.',
    Icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    gradient: 'from-green-500 to-teal-500',
  },

  // E-transfer processing states
  'pending request money transfer': {
    label: 'Awaiting E-transfer',
    description: 'Please send an e-transfer to complete your payment.',
    Icon: Clock,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    gradient: 'from-blue-500 to-cyan-500',
  },

  'finalizing request money transfer': {
    label: 'Processing E-transfer',
    description:
      "We've received your e-transfer and are processing the payment.",
    Icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    gradient: 'from-amber-500 to-orange-600',
  },

  // Failure state: Something went wrong
  FAILED: {
    label: 'Failed',
    description:
      'There was an issue with your transfer. Please contact support for assistance.',
    Icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    gradient: 'from-red-500 to-red-600',
  },

  // Fallback for any unknown statuses
  default: {
    label: 'Pending',
    description: 'The payment status is currently updating.',
    Icon: Clock,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    gradient: 'from-gray-500 to-slate-500',
  },
}

// Transform API configurations into CPN_SUPPORTED_ROUTES-like structure
export const transformConfigurationsToDynamicRoutes = (
  configurations: Array<{
    destinationCountry?: string
    destinationCurrency: string
    paymentMethodType: string
    amountLimit: { max: number; min: number; currency: string }
    lastUpdated: string
  }>
) => {
  const routesMap = new Map()

  // Group configurations by country
  configurations.forEach((config) => {
    const countryCode = config.destinationCountry ?? ''

    if (!routesMap.has(countryCode)) {
      routesMap.set(countryCode, {
        name: getCountryName(countryCode),
        currencies: new Map(),
        localCurrency: config.destinationCurrency,
        flag: getCountryFlag(countryCode),
        description: `${getPaymentMethodDisplayName(config.paymentMethodType)} payments`,
      })
    }

    const country = routesMap.get(countryCode)
    const currencyCode = config.destinationCurrency

    if (!country.currencies.has(currencyCode)) {
      const limits = config.amountLimit
      country.currencies.set(currencyCode, {
        code: currencyCode,
        paymentMethods: new Set(),
        defaultPaymentMethod: config.paymentMethodType as CpnPaymentMethod, // Will be updated after collecting all methods
        status: 'confirmed' as const,
        supportedBlockchains: new Set(),
        amountLimits: {
          min: limits.min,
          max: limits.max,
        },
        lastUpdated: config.lastUpdated,
      })
    }

    const currency = country.currencies.get(currencyCode)
    currency.paymentMethods.add(config.paymentMethodType as CpnPaymentMethod)
  })

  // Convert Maps to objects and Sets to arrays
  const result: Record<string, DynamicCpnRoute> = {}
  routesMap.forEach((country, countryCode) => {
    const currencies: DynamicCurrency[] = []
    country.currencies.forEach(
      (currency: {
        code: string
        paymentMethods: Set<CpnPaymentMethod>
        defaultPaymentMethod: CpnPaymentMethod
        status: 'confirmed' | 'limited'
        supportedBlockchains: Set<string>
        amountLimits: { min: number; max: number }
        lastUpdated: string
      }) => {
        const paymentMethodsArray = Array.from(currency.paymentMethods)
        // Use the fastest payment method as default
        const fastestMethod = getFastestPaymentMethod(paymentMethodsArray)

        currencies.push({
          code: currency.code,
          paymentMethods: paymentMethodsArray,
          defaultPaymentMethod: fastestMethod || currency.defaultPaymentMethod,
          status: currency.status,
          supportedBlockchains: Array.from(currency.supportedBlockchains),
          amountLimits: currency.amountLimits,
          lastUpdated: currency.lastUpdated,
        })
      }
    )

    result[countryCode] = {
      name: country.name,
      currencies,
      localCurrency: country.localCurrency,
      flag: country.flag,
      description: country.description,
    }
  })

  return result
}

// Helper functions for transformation
const getCountryName = (countryCode: string): string => {
  const countryNames: Partial<Record<CpnCountryCode, string>> = {
    BR: 'Brazil',
    HK: 'Hong Kong',
    MX: 'Mexico',
    CN: 'China',
    CO: 'Colombia',
    NG: 'Nigeria',
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    DE: 'Germany',
    FR: 'France',
    IT: 'Italy',
    ES: 'Spain',
    AU: 'Australia',
    JP: 'Japan',
    KR: 'South Korea',
    IN: 'India',
    SG: 'Singapore',
    TH: 'Thailand',
    MY: 'Malaysia',
    PH: 'Philippines',
    ID: 'Indonesia',
    VN: 'Vietnam',
  }
  return countryNames[countryCode] || countryCode
}

const getCountryFlag = (countryCode: string): string => {
  const countryFlags: Partial<Record<CpnCountryCode, string>> = {
    BR: '🇧🇷',
    HK: '🇭🇰',
    MX: '🇲🇽',
    CN: '🇨🇳',
    CO: '🇨🇴',
    NG: '🇳🇬',
    US: '🇺🇸',
    CA: '🇨🇦',
    GB: '🇬🇧',
    DE: '🇩🇪',
    FR: '🇫🇷',
    IT: '🇮🇹',
    ES: '🇪🇸',
    AU: '🇦🇺',
    JP: '🇯🇵',
    KR: '🇰🇷',
    IN: '🇮🇳',
    SG: '🇸🇬',
    TH: '🇹🇭',
    MY: '🇲🇾',
    PH: '🇵🇭',
    ID: '🇮🇩',
    VN: '🇻🇳',
  }
  return countryFlags[countryCode] || '🏳️'
}

const getPaymentMethodDisplayName = (paymentMethod: string): string => {
  const displayNames: Record<string, string> = {
    PIX: 'PIX',
    WIRE: 'Wire transfer',
    FPS: 'FPS',
    CHATS: 'CHATS',
    SPEI: 'SPEI',
    NEQUI: 'Nequi',
    'BANK-TRANSFER': 'Bank transfer',
    CIPS: 'CIPS',
    IMPS: 'IMPS',
    NEFT: 'NEFT',
    RTGS: 'RTGS',
    PESONET: 'PESONet',
    INSTAPAY: 'INSTAPAY',
  }
  return displayNames[paymentMethod] || paymentMethod
}

// API-based validation using CPN configurations
export const validateCurrencyCountryPaymentMethodFromConfigs = (
  configs: {
    destinationCurrency: string
    paymentMethodType: string
  }[],
  country: string,
  currency: string,
  paymentMethod: string
): {
  isValid: boolean
  suggestedCurrency?: string
  suggestedPaymentMethod?: CpnPaymentMethod
  reason?: string
} => {
  // If no configs returned, route is not supported
  if (!configs || configs.length === 0) {
    return {
      isValid: false,
      reason: `Route not supported: ${currency} to ${country} currency ${currency}`,
    }
  }

  // Extract available payment methods and currencies from configs
  const availablePaymentMethods = [
    ...new Set(configs.map((c) => c.paymentMethodType)),
  ]
  const availableCurrencies = [
    ...new Set(configs.map((c) => c.destinationCurrency)),
  ]

  // Check if currency is supported
  if (!availableCurrencies.includes(currency)) {
    return {
      isValid: false,
      suggestedCurrency: availableCurrencies[0],
      suggestedPaymentMethod: availablePaymentMethods[0] as CpnPaymentMethod,
      reason: `Currency ${currency} not supported for ${country}. Available: ${availableCurrencies.join(
        ', '
      )}`,
    }
  }

  // Check if payment method is supported
  if (!availablePaymentMethods.includes(paymentMethod)) {
    return {
      isValid: false,
      suggestedCurrency: currency,
      suggestedPaymentMethod: availablePaymentMethods[0] as CpnPaymentMethod,
      reason: `Payment method ${paymentMethod} not supported for ${country}. Available: ${availablePaymentMethods.join(
        ', '
      )}`,
    }
  }

  // Check if the specific combination exists
  const validCombo = configs.find(
    (config) =>
      config.paymentMethodType === paymentMethod &&
      config.destinationCurrency === currency
  )

  if (!validCombo) {
    return {
      isValid: false,
      suggestedCurrency: availableCurrencies[0],
      suggestedPaymentMethod: availablePaymentMethods[0] as CpnPaymentMethod,
      reason: `Combination ${paymentMethod} + ${currency} not supported for ${country}`,
    }
  }

  return { isValid: true }
}

// Priority order for displaying countries (higher priority = appears first)
export const COUNTRY_DISPLAY_PRIORITY: CpnCountryCode[] = [
  'NG',
  'HK',
  'MX',
  'BR',
  'CO',
  'IN',
]

// Dynamic version that works with API-fetched route data
export const getDynamicDestinationOptions = (
  dynamicRoutes: Record<string, DynamicCpnRoute>
) => {
  return Object.entries(dynamicRoutes)
    .flatMap(([countryCode, route]) =>
      route.currencies.map((currency) => {
        const fastestMethod = getFastestPaymentMethod(currency.paymentMethods)

        return {
          countryCode,
          currency: currency.code,
          name: route.name,
          flag: route.flag,
          paymentMethods: currency.paymentMethods,
          defaultPaymentMethod: fastestMethod || currency.defaultPaymentMethod,
          isLocal: currency.code === route.localCurrency,
          description: route.description,
          status: currency.status,
          amountLimits: currency.amountLimits,
          supportedBlockchains: currency.supportedBlockchains,
          lastUpdated: currency.lastUpdated,
        }
      })
    )
    .sort((a, b) => {
      const priorityA = COUNTRY_DISPLAY_PRIORITY.indexOf(
        a.countryCode as CpnCountryCode
      )
      const priorityB = COUNTRY_DISPLAY_PRIORITY.indexOf(
        b.countryCode as CpnCountryCode
      )
      if (priorityA !== -1 && priorityB !== -1) return priorityA - priorityB
      if (priorityA !== -1) return -1
      if (priorityB !== -1) return 1
      return a.name.localeCompare(b.name)
    })
}

export const getPaymentStatusInfo = (status?: string): StatusInfo => {
  if (!status) {
    return paymentStatusMap.default
  }
  return paymentStatusMap[status] || paymentStatusMap.default
}

// Failure reason mapping for user-friendly error messages
export const FAILURE_REASON_MESSAGES: Record<string, string> = {
  PAYMENT_EXPIRED:
    'Your payment session has expired. Please create a new payment.',
  INSUFFICIENT_FUNDS: 'Insufficient funds available to process this payment.',
  INVALID_RECIPIENT_DETAILS:
    'The recipient details provided are invalid or incomplete.',
  PAYMENT_REJECTED_BY_BANK: 'The payment was rejected by the recipient bank.',
  PAYMENT_CANCELLED: 'The payment was cancelled.',
  NETWORK_ERROR: 'A network error occurred while processing your payment.',
  PAYMENT_LIMIT_EXCEEDED: 'This payment exceeds the allowed limits.',
  INVALID_PAYMENT_METHOD:
    'The selected payment method is not supported for this transaction.',
  COMPLIANCE_CHECK_FAILED:
    'The payment could not be completed due to compliance requirements.',
  TECHNICAL_ERROR:
    'A technical error occurred. Please try again or contact support.',
  RECIPIENT_BANK_UNAVAILABLE: 'The recipient bank is currently unavailable.',
  INVALID_CURRENCY_PAIR: 'The currency conversion is not supported.',
  PAYMENT_TIMEOUT: 'The payment timed out during processing.',
  DUPLICATE_PAYMENT: 'This appears to be a duplicate payment.',
  REGULATORY_HOLD: 'The payment is on hold for regulatory review.',
  KYC_VERIFICATION_REQUIRED: 'Additional identity verification is required.',
  SANCTIONS_CHECK_FAILED:
    'The payment could not be completed due to sanctions screening.',
}

// Internal error codes that should not be displayed to users
export const HIDDEN_FAILURE_REASONS = new Set([
  'crypto_funds_pending',
  'internal_processing_error',
  'system_maintenance',
  'rate_limit_exceeded',
  'api_timeout',
  'database_error',
  'queue_processing_failed',
])

// Get user-friendly failure reason message
export const getFailureReasonMessage = (
  failureReason?: string
): string | null => {
  if (!failureReason) {
    return null
  }

  // Don't show internal error codes to users
  if (HIDDEN_FAILURE_REASONS.has(failureReason.toLowerCase())) {
    return null
  }

  // Return mapped message or a generic fallback
  return (
    FAILURE_REASON_MESSAGES[failureReason] ||
    FAILURE_REASON_MESSAGES[failureReason.toUpperCase()] ||
    'An unexpected error occurred. Please contact support for assistance.'
  )
}
